import { useCallback, useEffect, useMemo, useState } from 'react';
import { qbankApi } from '../lib/questionBankApi';
import { subjectLabel } from '../lib/questionBankApi';
import type { NamedOption, QuestionPage, QuestionView, UnitView } from '../lib/questionBankApi';
import { StatTile } from '../components/charts';

/**
 * THE QUESTION BANK.
 *
 * ⚠ THE FILTERS ARE THE FEATURE. A bank of nine hundred questions is a filing cabinet unless a
 * teacher can ask it "three-mark competency questions in World of Living that have never been on
 * a paper" and get an answer in one second — which is the actual question they have when they sit
 * down to write a test.
 *
 * ⚠ EVERY QUESTION SHOWS WHERE IT CAME FROM AND WHO APPROVED IT, and that is deliberate. The next
 * slice of this feature lets the assistant draft questions. From the moment that ships, "who wrote
 * this one?" is a question a head of department is entitled to ask about anything on a board
 * paper, and the answer has to be on the row rather than reconstructed.
 */
export function QuestionBankPage() {
  const [data, setData] = useState<QuestionPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const [institutionId, setInstitutionId] = useState<string>('');
  const [subjectId, setSubjectId] = useState<string>('');
  const [unitId, setUnitId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [typology, setTypology] = useState<string>('');
  const [marks, setMarks] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const PAGE = 25;

  const params = useMemo(() => {
    const p: Record<string, string> = { skip: String(page * PAGE), take: String(PAGE) };
    if (institutionId) p.institutionId = institutionId;
    if (subjectId) p.subjectId = subjectId;
    if (unitId) p.unitId = unitId;
    if (status) p.status = status;
    if (typology) p.typology = typology;
    if (marks) p.marks = marks;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [institutionId, subjectId, unitId, status, typology, marks, search, page]);

  const load = useCallback(() => {
    setLoading(true);
    return qbankApi
      .list(params)
      .then((r) => {
        setData(r);
        setFailed(false);
        if (!institutionId && r.institutionId) setInstitutionId(r.institutionId);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [params, institutionId]);

  useEffect(() => {
    let alive = true;
    qbankApi
      .list(params)
      .then((r) => {
        if (!alive) return;
        setData(r);
        setFailed(false);
        if (!institutionId && r.institutionId) setInstitutionId(r.institutionId);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // ⚠ Any filter change resets to the first page. Without this, narrowing a filter while on page
  // four shows an empty list and reads as "no results".
  const setFilter = (fn: () => void) => { fn(); setPage(0); };

  // ⚠ Only the drafts actually on screen. A bank with two hundred drafts across six subjects and
  // one "approve everything" button is how a whole subject's unreviewed questions end up on a
  // child's paper; approving what you are looking at is a decision, approving what you are not is
  // an accident.
  const draftsShown = (data?.questions ?? []).filter((q) => q.status === 'Draft');

  const approveShown = () => {
    if (draftsShown.length === 0) return;
    if (!window.confirm(
      `Approve ${draftsShown.length} question${draftsShown.length === 1 ? '' : 's'} shown here?\n\n`
      + 'Approved questions can go straight onto a paper. Anything you wrote yourself will be '
      + 'skipped — those need somebody else.')) return;

    setBusy('bulk');
    qbankApi
      .approveMany(draftsShown.map((q) => q.id))
      .then((r) => {
        setFlash(r.skipped.length === 0
          ? `Approved ${r.approved}.`
          // The domain's own sentence, once, rather than the same refusal repeated N times.
          : `Approved ${r.approved}. ${r.skipped.length} skipped — ${r.skipped[0].reason}`);
        return load();
      })
      .catch((e: unknown) => setFlash(messageOf(e) ?? 'Could not approve those.'))
      .finally(() => setBusy(null));
  };

  const approve = (id: string) => {
    setBusy(id);
    qbankApi
      .approve(id)
      .then(() => { setFlash('Approved.'); return load(); })
      .catch((e: unknown) => setFlash(messageOf(e) ?? 'Could not approve that question.'))
      .finally(() => setBusy(null));
  };

  const retire = (id: string) => {
    setBusy(id);
    qbankApi
      .retire(id)
      .then(() => { setFlash('Retired. It stays in the bank and will not be used again.'); return load(); })
      .catch(() => setFlash('Could not retire that question.'))
      .finally(() => setBusy(null));
  };

  if (loading && !data) return <p style={{ color: 'var(--ink-3)' }}>Opening the question bank…</p>;
  if (failed || !data) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load the question bank.</div>;
  }

  const pages = Math.max(1, Math.ceil(data.total / PAGE));

  return (
    <div className="qb">
      <header className="qb-head">
        <h1>Question bank</h1>
        <p className="qb-sub">
          Tagged by unit, by what the question tests, by difficulty and by Bloom level — because a
          paper cannot be built to the board's specification from questions that are only filed by
          chapter.
        </p>
      </header>

      <div className="qb-tiles">
        <StatTile label="Approved" value={count(data.counts.approved)} foot="usable on a paper" />
        <StatTile label="In draft" value={count(data.counts.draft)}
          foot={data.counts.draft > 0 ? 'need a second pair of eyes' : 'nothing waiting'} />
        <StatTile label="Never used" value={count(data.counts.neverUsed)} foot="fresh" />
        <StatTile label="Retired" value={count(data.counts.retired)} foot="kept, not deleted" />
      </div>

      {data.note && <div className="qb-note">{data.note}</div>}
      {draftsShown.length > 0 && (
        <div className="card" style={{
          padding: '10px 14px', marginBottom: 10, display: 'flex', gap: 12,
          alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            <strong>{draftsShown.length}</strong> draft{draftsShown.length === 1 ? '' : 's'} on this
            page. A paper can only use approved questions.
          </span>
          <button className="btn btn-primary" onClick={approveShown} disabled={busy === 'bulk'}>
            {busy === 'bulk' ? 'Approving…' : `Approve all ${draftsShown.length} shown`}
          </button>
        </div>
      )}

      {flash && <div className="qb-flash">{flash}</div>}

      <div className="card qb-controls">
        {data.institutions.length > 1 && (
          <label>
            <span>Institution</span>
            <select value={institutionId} onChange={(e) => setFilter(() => { setInstitutionId(e.target.value); setSubjectId(''); setUnitId(''); })}>
              {data.institutions.map((i: NamedOption) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        )}

        <label>
          <span>Subject</span>
          <select value={subjectId} onChange={(e) => setFilter(() => { setSubjectId(e.target.value); setUnitId(''); })}>
            <option value="">All subjects</option>
            {/* ⚠ Code shown only where names collide — see subjectLabel. */}
            {data.subjects.map((s) => <option key={s.id} value={s.id}>{subjectLabel(s, data.subjects)}</option>)}
          </select>
        </label>

        <label>
          <span>Unit</span>
          <select value={unitId} onChange={(e) => setFilter(() => setUnitId(e.target.value))}>
            <option value="">All units</option>
            {data.units.map((u: UnitView) => (
              <option key={u.id} value={u.id}>{u.name} ({u.boardMarks} marks)</option>
            ))}
          </select>
        </label>

        <label>
          <span>Tests</span>
          <select value={typology} onChange={(e) => setFilter(() => setTypology(e.target.value))}>
            <option value="">Any</option>
            <option value="Competency">Competency-based</option>
            <option value="Objective">Objective</option>
            <option value="Descriptive">Short and long answer</option>
          </select>
        </label>

        <label>
          <span>Marks</span>
          <select value={marks} onChange={(e) => setFilter(() => setMarks(e.target.value))}>
            <option value="">Any</option>
            {[1, 2, 3, 4, 5].map((m) => <option key={m} value={String(m)}>{m} mark{m > 1 ? 's' : ''}</option>)}
          </select>
        </label>

        <label>
          <span>Status</span>
          <select value={status} onChange={(e) => setFilter(() => setStatus(e.target.value))}>
            <option value="">Any</option>
            <option value="Approved">Approved</option>
            <option value="Draft">Draft</option>
            <option value="Retired">Retired</option>
          </select>
        </label>

        <label className="qb-wide">
          <span>Search</span>
          <input value={search} placeholder="a word in the question, or a source"
            onChange={(e) => setFilter(() => setSearch(e.target.value))} />
        </label>
      </div>

      {/* ---------------------------------------------------------------- units */}
      {data.units.length > 0 && (
        <div className="card qb-units">
          <h2>Syllabus units</h2>
          <p className="qb-sub">
            The board's marks per unit, and what the bank holds against each. This is the split a
            paper set by hand gets wrong most often, because a teacher sets what they have just
            finished teaching.
          </p>
          <div className="qb-unit-grid">
            {data.units.map((u) => (
              <button key={u.id}
                className={`qb-unit ${unitId === u.id ? 'is-on' : ''}`}
                onClick={() => setFilter(() => setUnitId(unitId === u.id ? '' : u.id))}>
                <span className="qb-unit-name">{u.name}</span>
                <span className="qb-unit-marks">{u.boardMarks} marks</span>
                <span className="qb-unit-count">
                  {count(u.approvedQuestions)} approved{u.draftQuestions > 0 ? ` · ${count(u.draftQuestions)} draft` : ''}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------- list */}
      <div className="qb-list">
        {data.questions.length === 0 && (
          <div className="card" style={{ padding: 18, color: 'var(--ink-3)' }}>
            Nothing matches those filters.
          </div>
        )}

        {data.questions.map((q) => (
          <QuestionCard key={q.id} q={q}
            open={open === q.id}
            onToggle={() => setOpen(open === q.id ? null : q.id)}
            busy={busy === q.id}
            onApprove={() => approve(q.id)}
            onRetire={() => retire(q.id)} />
        ))}
      </div>

      {pages > 1 && (
        <div className="qb-pager">
          <button className="btn" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
          <span>Page {page + 1} of {count(pages)} · {count(data.total)} questions</span>
          <button className="btn" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}

function QuestionCard({ q, open, onToggle, busy, onApprove, onRetire }: {
  q: QuestionView; open: boolean; onToggle: () => void; busy: boolean;
  onApprove: () => void; onRetire: () => void;
}) {
  return (
    <div className={`card qb-q status-${q.status.toLowerCase()}`}>
      {/* ⚠ THREE ROWS THAT COLLAPSE TO TWO, not a three-column row. The button used to hold the
          row open at every width, squeezing the question itself into a 165px ribbon twelve lines
          tall at 390px with dead space beside the button. The question is the content; it gets the
          full width of the card and the two controls share the line above it.
          ⚠ Order is visual only — the text stays before the button in the DOM, so it is still read
          and tabbed in the order it makes sense in. */}
      <div className="qb-q-head" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <span
          className="qb-marks"
          style={{
            order: 0,
            width: 'auto', height: 'auto', borderRadius: 999, padding: '3px 9px',
            whiteSpace: 'nowrap',
          }}
        >
          {q.marks} {q.marks === 1 ? 'mark' : 'marks'}
        </span>
        <div className="qb-q-main" style={{ order: 2, flex: '1 1 100%' }}>
          <p className="qb-q-text">{q.text}</p>
          <div className="qb-chips">
            <span className={`qb-chip typ-${q.typology.toLowerCase()}`}>{q.typologyLabel}</span>
            <span className="qb-chip">{q.typeLabel}</span>
            <span className="qb-chip">{q.difficulty}</span>
            <span className="qb-chip">{q.bloom}</span>
            {q.unitName && <span className="qb-chip unit">{q.unitName}</span>}
            {q.status !== 'Approved' && <span className={`qb-chip st-${q.status.toLowerCase()}`}>{q.status}</span>}
            {q.origin === 'AiDrafted' && <span className="qb-chip ai">Drafted by the assistant</span>}
            {!q.partsAddUp && (
              <span className="qb-chip bad">
                Sub-parts do not add up to {q.marks} — this question cannot go on a paper
              </span>
            )}
          </div>
          <p className="qb-q-foot">
            {q.usageCount === 0 ? 'Never used' : `Used ${count(q.usageCount)} time${q.usageCount > 1 ? 's' : ''}`}
            {q.lastUsedOn ? <>, last on <span style={{ whiteSpace: 'nowrap' }}>{pretty(q.lastUsedOn)}</span></> : ''}
            {q.approvedByName ? ` · approved by ${q.approvedByName}` : ''}
            {q.source ? ` · ${q.source}` : ''}
          </p>
        </div>
        <button className="btn btn-quiet qb-expand" style={{ order: 1, marginLeft: 'auto' }} onClick={onToggle}>
          {open ? 'Hide' : 'Answer'}
        </button>
      </div>

      {open && (
        <div className="qb-q-body">
          {q.options.length > 0 && (
            <ol className="qb-options">
              {q.options.map((o) => (
                <li key={o.label} className={o.isCorrect ? 'is-correct' : ''}>
                  ({o.label.toLowerCase()}) {o.text}
                </li>
              ))}
            </ol>
          )}

          {q.parts.length > 0 && (
            <ol className="qb-parts">
              {q.parts.map((p) => (
                <li key={p.id}>
                  <span className="qb-part-marks">{p.marks}</span> {p.text}
                  {p.answerKey && <em className="qb-ans"> {p.answerKey}</em>}
                </li>
              ))}
            </ol>
          )}

          {q.answerKey && <p className="qb-ans"><strong>Answer.</strong> {q.answerKey}</p>}

          <div className="qb-q-actions">
            {q.status === 'Draft' && (
              <button className="btn btn-primary" disabled={busy} onClick={onApprove}>
                {busy ? 'Working…' : 'Approve'}
              </button>
            )}
            {q.status !== 'Retired' && (
              <button className="btn btn-quiet" disabled={busy} onClick={onRetire}>Retire</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ⚠ Indian grouping. A bank this size prints five-figure totals, and "1284" is a number a reader
 * has to count the digits of. `en-IN` puts them where a school expects them: 1,284 — and 1,52,300.
 */
function count(n: number): string {
  return n.toLocaleString('en-IN');
}

/**
 * ⚠ Explicit local midnight. A bare `new Date('2025-09-18')` is parsed as UTC and reads back as
 * the 17th for anybody west of Greenwich.
 */
function pretty(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** ⚠ The API returns the DOMAIN's own sentence on a rejected approval. Show it verbatim. */
function messageOf(e: unknown): string | null {
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return null;
}
